import { useState, type FormEvent } from "react";

const SUBSCRIBE_URL = "https://readdy.ai/api/form/dah959bp14a1h3mr6fs0";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(SUBSCRIBE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const succeeded = response.ok && parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!succeeded || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <section className="bg-background-100 px-4 py-12 md:px-6 md:py-16" aria-label="订阅爆料速递">
      <div className="shell">
      <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 lg:grid-cols-5">
        <div className="bg-primary-500 p-6 md:p-8 lg:col-span-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-[11px] font-label font-bold text-background-50">
            <i className="ri-notification-3-line" aria-hidden="true" />
            Newsletter
          </span>
          <h2 className="mt-5 font-heading text-2xl font-black leading-tight text-background-50 md:text-3xl">
            订阅爆料速递
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-background-100">
            每天一封汇总邮件，只保留当天最值得跟进的事件与时间线更新，不发无关内容。
          </p>
          <ul className="mt-6 space-y-3">
            {["当日新增爆料汇总", "重要事件时间线更新提醒", "首发线索优先推送"].map((text) => (
              <li key={text} className="flex items-center gap-2.5 text-sm text-background-50">
                <i className="ri-checkbox-circle-line text-accent-300" aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-background-50 p-6 md:p-8 lg:col-span-3">
          <form id="subscribe-form" data-readdy-form action={SUBSCRIBE_URL} method="post" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="sub-name" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                  称呼
                </label>
                <input
                  id="sub-name"
                  name="name"
                  type="text"
                  placeholder="怎么称呼你"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="sub-email" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="sub-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="sub-interest" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                  最关注的方向
                </label>
                <select
                  id="sub-interest"
                  name="interest"
                  defaultValue="娱乐圈"
                  className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="娱乐圈">娱乐圈</option>
                  <option value="网红圈">网红圈</option>
                  <option value="影视瓜">影视瓜</option>
                  <option value="情感瓜">情感瓜</option>
                  <option value="首发追踪">首发追踪</option>
                </select>
              </div>
            </div>

            <input
              type="text"
              name="website_alt"
              className="newsletter-note-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-3 text-sm font-label font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "sending" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                  提交中
                </>
              ) : (
                <>
                  立即订阅
                  <i className="ri-send-plane-line" aria-hidden="true" />
                </>
              )}
            </button>

            {status === "success" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-900">
                <i className="ri-checkbox-circle-fill text-accent-700" aria-hidden="true" />
                订阅成功，爆料速递会发送到你填写的邮箱。
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3.5 py-2.5 text-sm text-primary-800">
                <i className="ri-error-warning-fill text-primary-600" aria-hidden="true" />
                {formError}
              </p>
            )}
            <p className="mt-4 text-xs text-foreground-500">我们只用它发送爆料速递，随时可以退订。</p>
          </form>
        </div>
      </div>
      </div>
    </section>
  );
}